import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Switch,
  Paper,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Avatar,
  Divider,
  InputAdornment,
} from '@mui/material';
import { IoSearch } from 'react-icons/io5';
import { enqueueSnackbar } from 'notistack';
import {
  getRolesInit,
  createOrUpdateRole,
  getRoleDetailsForEdit,
  updateRolePermissions,
  getUserPermissions,
} from "../../api's/ManagementApi";

const EditRoleDialog = ({ open, onClose, role, onSave }) => {
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [groupsAssigned, setGroupsAssigned] = useState(0);
  const [permissions, setPermissions] = useState([]);
  const [selectedTab, setSelectedTab] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groupListOpen, setGroupListOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [initialPermissions, setInitialPermissions] = useState([]);
  const [permissionSearch, setPermissionSearch] = useState('');

  useEffect(() => {
    if (open) {
      setPermissionSearch('');
      if (role && role.roleId) {
        // Editing existing role
        fetchRoleDetails(role.roleId);
      } else {
        // Adding new role
        setRoleName('');
        setDescription('');
        setGroupsAssigned(0);
        setSelectedGroups([]);
        fetchPermissions();
      }
    }
  }, [open, role]);

  const fetchRoleDetails = async (roleId) => {
    setLoading(true);
    try {
      const [roleResponse, initResponse] = await Promise.all([getRoleDetailsForEdit(roleId), getRolesInit()]);

      // Set available groups from init API
      if (initResponse.status === 200 && initResponse.data.groups) {
        setGroups(initResponse.data.groups);
      }

      // Set role basic info
      if (roleResponse.status === 200 && roleResponse.data) {
        const roleData = roleResponse.data;
        setRoleName(roleData.roleName || '');
        setDescription(roleData.description || '');
        setGroupsAssigned(roleData.groupCount || 0);

        // Set assigned groups from groups array
        if (roleData.groups && Array.isArray(roleData.groups)) {
          const groupIds = roleData.groups.map((group) => group.groupId);
          setSelectedGroups(groupIds);
        }

        // Process permissions from the edit API response
        if (roleData.permissions && Array.isArray(roleData.permissions)) {
          const permissionsData = roleData.permissions.map((perm) => ({
            id: perm.permissionId,
            name: perm.permissionName,
            description: perm.description || '',
            category: perm.permissionType || categorizePermission(perm.permissionName),
            enabled: perm.allowed === true,
          }));
          setPermissions(permissionsData);

          // Store initial permissions state to detect changes
          setInitialPermissions(permissionsData.map((p) => ({ id: p.id, enabled: p.enabled })));

          // Extract distinct permission types dynamically
          const distinctTabs = [...new Set(permissionsData.map((p) => p.category).filter(Boolean))];
          setTabs(distinctTabs);

          // Set first tab as selected if available
          if (distinctTabs.length > 0) {
            setSelectedTab(distinctTabs[0]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching role details:', error);
      enqueueSnackbar('Failed to load role details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await getRolesInit();
      if (response.status === 200 && response.data.permissions) {
        setGroups(response.data.groups);
        const permissionsData = response.data.permissions.map((perm) => ({
          id: perm.permissionId,
          name: perm.permissionName,
          description: perm.description || '',
          category: perm.permissionType || categorizePermission(perm.permissionName),
          enabled: false, // Default to false, will be set based on role data
        }));
        setPermissions(permissionsData);

        // Extract distinct permission types dynamically
        const distinctTabs = [...new Set(permissionsData.map((p) => p.category).filter(Boolean))];
        setTabs(distinctTabs);

        // Always select first tab when tabs are loaded
        if (distinctTabs.length > 0) {
          setSelectedTab(distinctTabs[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      enqueueSnackbar('Failed to load permissions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const categorizePermission = (permissionName) => {
    const name = permissionName.toLowerCase();
    if (name.includes('pipeline') || name.includes('workflow')) {
      return 'ETL Pipelines';
    }
    if (name.includes('approve') || name.includes('view')) {
      return 'Governance';
    }
    return 'Admin Tools';
  };

  const handlePermissionToggle = (permissionId) => {
    setPermissions((prev) =>
      prev.map((perm) => (perm.id === permissionId ? { ...perm, enabled: !perm.enabled } : perm))
    );
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      enqueueSnackbar('Role name is required', { variant: 'error' });
      return;
    }

    if (!description.trim()) {
      enqueueSnackbar('Description is required', { variant: 'error' });
      return;
    }

    // Validate role name contains only alphabets and spaces
    const roleNameRegex = /^[A-Za-z_\s]+$/;
    if (!roleNameRegex.test(roleName.trim())) {
      enqueueSnackbar('Role name can only contain alphabets and spaces', { variant: 'error' });
      return;
    }

    const enabledPermissions = permissions.filter((p) => p.enabled);
    if (enabledPermissions.length === 0) {
      enqueueSnackbar('Please select at least one permission', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      const roleData = {
        roleName: roleName.trim(),
        description: description.trim(),
        permissionIds: enabledPermissions.map((p) => p.id),
        groupIds: selectedGroups,
      };

      let response;
      const isEditMode = role && role.roleId;

      if (isEditMode) {
        // Update existing role
        response = await updateRolePermissions(role.roleId, roleData);
      } else {
        // Create new role
        response = await createOrUpdateRole(roleData);
      }

      if (response.status === 200 || response.status === 201) {
        // Check if permissions were changed during edit
        if (isEditMode) {
          const permissionsChanged = initialPermissions.some((initial) => {
            const current = permissions.find((p) => p.id === initial.id);
            return current && current.enabled !== initial.enabled;
          });

          if (permissionsChanged) {
            // Refresh user permissions in sessionStorage
            try {
              const email = sessionStorage.getItem('email');
              const permissionsResponse = await getUserPermissions(email);
              const updatedPermissions = permissionsResponse?.permissions || [];
              sessionStorage.setItem('userPermissions', JSON.stringify(updatedPermissions));
            } catch (permError) {
              console.error('Failed to refresh user permissions:', permError);
            }
          }
        }

        enqueueSnackbar(isEditMode ? 'Role updated successfully' : 'Role created successfully', { variant: 'success' });

        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving role:', error);
      if (error.response?.status === 409) {
        enqueueSnackbar(error.response?.data?.message || 'A role with the same name already exists', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(error.response?.data?.message || 'Failed to save role', { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const getPermissionsByCategory = (category) => {
    return permissions.filter((p) => p.category === category);
  };

  const sortPermissionsByName = (list) =>
    [...list].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );

  const getFilteredPermissionsByCategory = (category) => {
    const byCategory = getPermissionsByCategory(category);
    const sorted = sortPermissionsByName(byCategory);
    if (!permissionSearch.trim()) return sorted;
    const term = permissionSearch.trim().toLowerCase();
    return sortPermissionsByName(
      sorted.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(term)) ||
          (p.description && p.description.toLowerCase().includes(term)) ||
          formatPermissionName(p.name || '').toLowerCase().includes(term)
      )
    );
  };

  const formatPermissionName = (name) => {
    return name
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleViewGroupList = async () => {
    setGroupListOpen(true);
  };

  const handleGroupToggle = (groupId) => {
    setSelectedGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
  };

  const handleGroupListClose = () => {
    setGroupListOpen(false);
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = ['#1976d2', '#2e7d32', '#d32f2f', '#f57c00', '#7b1fa2', '#0097a7'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh',
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3, flexShrink: 0 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: '#1a1a1a',
            fontSize: '22px',
            mb: 0.5,
          }}
        >
          {role ? `Edit Role: ${role.roleName}` : 'Add Role'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#666',
            fontSize: '13px',
            lineHeight: 1.5,
          }}
        >
          Adjust what this role can do across ETL pipelines and governance features.
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5, pb: 2.5, px: 3, overflow: 'auto', flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={40} thickness={3.5} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2.5 }}>
            {/* Left Section - Role Details */}
            <Box
              sx={{
                flex: '0 0 360px',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.5,
                  fontWeight: 700,
                  fontSize: '12px',
                  color: '#1a1a1a',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                }}
              >
                Role Details
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    color: '#333',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  Role Name *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder="e.g. Data Analyst"
                  disabled={!!role}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      '&:hover fieldset': {
                        borderColor: '#00043C',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00043C',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    color: '#333',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  Description *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={description}
                  required
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the role's purpose and responsibilities..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      '&:hover fieldset': {
                        borderColor: '#00043C',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00043C',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    color: '#333',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  Groups Assigned
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e8e8e8',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#d0d0d0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px', color: '#1a1a1a' }}>
                      {groupsAssigned}
                    </Typography>
                    <Chip
                      label={groupsAssigned === 1 ? 'group' : 'groups'}
                      size="small"
                      sx={{
                        backgroundColor: groupsAssigned === 0 ? '#FFF3E0' : '#E3F2FD',
                        color: groupsAssigned === 0 ? '#EF6C00' : '#1565C0',
                        fontWeight: 600,
                        fontSize: '11px',
                        height: '20px',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    onClick={handleViewGroupList}
                    sx={{
                      color: '#00043C',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: '12px',
                      fontWeight: 600,
                      gap: 0.5,
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#000652',
                      },
                    }}
                  >
                    {groupsAssigned === 0 ? 'Assign groups' : 'Manage groups'} →
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.5,
                  fontWeight: 700,
                  fontSize: '12px',
                  color: '#1a1a1a',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                }}
              >
                Permissions
              </Typography>

              {/* Modern Tab Pills */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                {tabs.map((tab) => (
                  <Chip
                    key={tab}
                    label={tab}
                    onClick={() => setSelectedTab(tab)}
                    sx={{
                      px: 2,
                      py: 2,
                      height: 'auto',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      backgroundColor: selectedTab === tab ? '#00043C' : 'white',
                      color: selectedTab === tab ? 'white' : '#666',
                      border: selectedTab === tab ? 'none' : '1px solid #e0e0e0',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: selectedTab === tab ? '0 2px 8px rgba(0, 4, 60, 0.25)' : 'none',
                      '&:hover': {
                        backgroundColor: selectedTab === tab ? '#000652' : '#f0f2ff',
                        borderColor: selectedTab === tab ? 'transparent' : '#00043C',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  />
                ))}
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="Search permissions by name or description..."
                value={permissionSearch}
                onChange={(e) => setPermissionSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IoSearch style={{ color: '#999' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    fontSize: '13px',
                    '&:hover fieldset': { borderColor: '#00043C' },
                    '&.Mui-focused fieldset': { borderColor: '#00043C', borderWidth: '2px' },
                  },
                }}
              />

              {/* Permissions List */}
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#fafafa',
                  borderRadius: '10px',
                  border: '1px solid #e8e8e8',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.25,
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e8e8e8',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#999',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '10px',
                      letterSpacing: '0.8px',
                    }}
                  >
                    Permission ({getPermissionsByCategory(selectedTab).length})
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#999',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '10px',
                      letterSpacing: '0.8px',
                    }}
                  >
                    Allowed
                  </Typography>
                </Box>

                <Box
                  sx={{
                    maxHeight: '380px',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#d0d0d0',
                      borderRadius: '3px',
                      '&:hover': {
                        background: '#b0b0b0',
                      },
                    },
                  }}
                >
                  {getFilteredPermissionsByCategory(selectedTab).length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        {permissionSearch.trim()
                          ? 'No permissions match your search. Try a different term.'
                          : 'No permissions in this category.'}
                      </Typography>
                    </Box>
                  ) : (
                  getFilteredPermissionsByCategory(selectedTab).map((permission, index) => (
                    <Box
                      key={permission.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 2,
                        py: 1.5,
                        backgroundColor: permission.enabled ? '#f0f2ff' : 'white',
                        borderBottom:
                          index === getFilteredPermissionsByCategory(selectedTab).length - 1 ? 'none' : '1px solid #f0f0f0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: permission.enabled ? '#e8eaff' : '#fafafa',
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, pr: 1.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#1a1a1a',
                            mb: 0.25,
                            fontSize: '13px',
                          }}
                        >
                          {formatPermissionName(permission.name)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#666',
                            display: 'block',
                            fontSize: '12px',
                            lineHeight: 1.4,
                          }}
                        >
                          {permission.description || 'No description available'}
                        </Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={permission.enabled}
                        onChange={() => handlePermissionToggle(permission.id)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#00043C',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#00043C',
                          },
                        }}
                      />
                    </Box>
                  ))
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          backgroundColor: '#fafafa',
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving || loading}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: '8px',
            color: '#43425D',
            border: '1px solid #43425D',
            '&:hover': {
              borderColor: '#43425D',
              backgroundColor: 'rgba(67, 66, 93, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: '8px',
            backgroundColor: '#00043C',
            boxShadow: '0 4px 14px rgba(0, 4, 60, 0.25)',
            '&:hover': {
              backgroundColor: '#000652',
              boxShadow: '0 6px 20px rgba(0, 4, 60, 0.35)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              background: '#e0e0e0',
              color: '#999',
            },
          }}
        >
          {saving ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              Saving...
            </>
          ) : role ? (
            'Save Changes'
          ) : (
            'Create Role'
          )}
        </Button>
      </DialogActions>

      <Dialog
        open={groupListOpen}
        onClose={handleGroupListClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, flexShrink: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Assign Groups to Role
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
            Select groups to assign this role
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, overflow: 'auto', flex: 1 }}>
          {loadingGroups ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              {groups.map((group) => {
                const groupName = group.groupName || group.name || 'Unnamed Group';
                return (
                  <ListItem
                    key={group.groupId}
                    sx={{
                      px: 0,
                      py: 1,
                      borderBottom: '1px solid #f0f0f0',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedGroups.includes(group.groupId)}
                          onChange={() => handleGroupToggle(group.groupId)}
                          sx={{
                            '&.Mui-checked': {
                              color: '#00043C',
                            },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: '12px',
                              bgcolor: getAvatarColor(groupName),
                            }}
                          >
                            {getInitials(groupName)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {groupName}
                            </Typography>
                            {group.description && (
                              <Typography variant="caption" sx={{ color: '#666' }}>
                                {group.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0', flexShrink: 0, gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: '#888', fontSize: '13px', alignSelf: 'center', mr: 'auto' }}>
            {selectedGroups.length === 0
              ? 'No groups selected'
              : `${selectedGroups.length} group${selectedGroups.length === 1 ? '' : 's'} selected`}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleGroupListClose}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: '8px',
              color: '#43425D',
              border: '1px solid #43425D',
              '&:hover': {
                borderColor: '#43425D',
                backgroundColor: 'rgba(67, 66, 93, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setGroupsAssigned(selectedGroups.length);
              handleGroupListClose();
            }}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: '8px',
              backgroundColor: '#00043C',
              boxShadow: '0 2px 8px rgba(0, 4, 60, 0.25)',
              '&:hover': {
                backgroundColor: '#000652',
                boxShadow: '0 4px 12px rgba(0, 4, 60, 0.35)',
              },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EditRoleDialog;
